import { ContentDataPost } from '../../types/content/content'
import axios from 'axios'
import api from '../api'

const CONTENT_UPLOAD_TIMEOUT_MS = 60000
const DIRECT_CONTENT_UPLOAD_TIMEOUT_MS = 120000

const uploadThumbnailDirectly = async (file?: File | null): Promise<string | null> => {
  if (!file) return null

  try {
    const { data } = await api.post('/content/thumbnail-upload-url/', {
      filename: file.name,
      content_type: file.type || 'application/octet-stream',
      size: file.size,
    })
    await axios.put(data.url, file, {
      headers: data.headers,
      timeout: DIRECT_CONTENT_UPLOAD_TIMEOUT_MS,
    })
    return data.key
  } catch {
    return null
  }
}

const withDirectThumbnailUpload = async <T extends Record<string, any>>(content: T): Promise<T> => {
  const thumbnail = content.thumbnail
  if (!(thumbnail instanceof File)) return content

  const uploadKey = await uploadThumbnailDirectly(thumbnail)
  if (!uploadKey) return content

  const { thumbnail: _thumbnail, ...rest } = content
  return {
    ...rest,
    thumbnail_upload_key: uploadKey,
  } as T
}

export const fetchContentList = async ({
  page,
  pageSize,
  search,
  filters,
  assigned,
  assigned_to,
  started, 
  completed,
  completed_by,
}: {
  page: number
  search: string
  pageSize: number
  filters: { [key: string]: string[] }
  assigned?: boolean
  assigned_to?: string
  started?: boolean
  completed?: boolean
  completed_by? : string[]
}) => {
  const params: Record<string, unknown> = {
    page,
    search,
    page_size: pageSize,
    expand: 'author',
    fields: '*,author.first_name,author.last_name',
    assigned: assigned ? 'true' : undefined,
    assigned_to: assigned_to ? assigned_to : undefined,
    started: started ? 'true' : undefined,
    completed: completed ? 'true' : undefined,
    completed_by: completed_by ? completed_by.join(',') : undefined,
  }
  Object.entries(filters).forEach(([key, values]) => {
    if (values.length > 1) {
      params[`${key}__in`] = values.join(',')
    } else if (values.length === 1) {
      params[key] = values[0]
    }
  })
  const res = await api.get('/content/', { params })
  return res.data
}

export const fetchPublishedContentList = async ({
  page,
  pageSize,
  search,
  filters,
  status,
}: {
  page: number
  search: string
  pageSize: number
  filters: { [key: string]: string[] }
  status: string
}) => {
  const params: Record<string, unknown> = {
    page,
    search,
    page_size: pageSize,
    expand: 'author',
    fields: '*,author.first_name,author.last_name',
    status: status,
  }
  Object.entries(filters).forEach(([key, values]) => {
    if (values.length > 1) {
      params[`${key}__in`] = values.join(',')
    } else if (values.length === 1) {
      params[key] = values[0]
    }
  })
  const res = await api.get('/content/', { params })
  return res.data
}

export const addContent = async (content: ContentDataPost) => {
  const payload = await withDirectThumbnailUpload(content as unknown as Record<string, any>)
  const res = await api.post('/content/', payload, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: CONTENT_UPLOAD_TIMEOUT_MS,
  })
  return res.data
}

export const fetchContent = async (id: string) => {
  const res = await api.get(`/content/${id}/`)
  return res.data
}

export const updateContent = async (id: string, content: ContentDataPost) => {
  const payload = await withDirectThumbnailUpload(content as unknown as Record<string, any>)
  const res = await api.patch(`/content/${id}/`, payload, {
    timeout: CONTENT_UPLOAD_TIMEOUT_MS,
  })
  return res.data
}

export const patchContent = async (id: string, content: any) => {
  const payload = await withDirectThumbnailUpload(content)
  const res = await api.patch(`/content/${id}/`, payload, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: CONTENT_UPLOAD_TIMEOUT_MS,
  })
  return res.data
}

export const publishContent = async (id: string) => {
  const res = await api.patch(`/content/${id}/publish/`)
  return res.data
}

export const completeContent = async (id: string, occurrenceId?: string | null) => {
  const res = await api.patch(`/content/${id}/complete/`, occurrenceId ? { occurrence_id: occurrenceId } : undefined)
  return res.data
}

export const submitContent = async (id: string) => {
  const res = await api.patch(`/content/${id}/submit/`)
  return res.data
}

export const deleteContent = async (id: string) => {
  const res = await api.delete(`/content/${id}/`)
  return res.data
}

export type ContentAssignmentType = 'suggested' | 'required'
export type ContentRecipientType = 'user' | 'all' | 'job_profile' | 'team'

export const assignContent = async (data: {
  frequency?: 'monthly' | 'yearly'
  first_due_date?: string
  contents: string[]
  assignment_type: ContentAssignmentType
  recipient_type: ContentRecipientType
  recipients?: string[]
  recipient_team?: string
  recipient_job_profile?: string
}) => {
  const res = await api.post('/content/assignments/', data)
  return res.data
}

export const fetchTopics = async () => {
  const res = await api.get('/content/topics/')
  return res.data
}

export const getAllContentStatistics = async (params: {
  page: number
  page_size: number
  search: string
  has_pending?: boolean
  /** Comma separated content types; omitted means every type. */
  content_type?: string
}) => {
  const response = await api.get('/content/statistics/', { params })
  return response.data
}

export const getContentAssignedOverview = async (contentId: string) => {
  const response = await api.get(`/content/${contentId}/assigned_overview/`)
  return response.data
}

export const getContentPendingOverview = async (contentId: string) => {
  const response = await api.get(`/content/${contentId}/pending_overview/`)
  return response.data
}

export const getContentCompletedOverview = async (contentId: string) => {
  const response = await api.get(`/content/${contentId}/completed_overview/`)
  return response.data
}

export interface ContentStatisticsExportParams {
  search?: string
  has_pending?: boolean
  content_id?: string
  content_type?: string
  status?: 'completed' | 'pending' | 'all'
}

const downloadStatisticsExport = async (
  format: 'csv' | 'xls',
  params: ContentStatisticsExportParams,
) => {
  const response = await api.get(`/content/statistics/export-${format}/`, {
    params,
    responseType: 'blob',
  })

  const mimeType =
    format === 'csv'
      ? 'text/csv;charset=utf-8;'
      : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  const blob = new Blob([response.data], { type: mimeType })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url

  const contentDisposition = response.headers['content-disposition']
  let filename = `content_statistics_export.${format === 'csv' ? 'csv' : 'xlsx'}`
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1]
    }
  }

  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

export const exportContentStatisticsCsv = (params: ContentStatisticsExportParams) =>
  downloadStatisticsExport('csv', params)

export const exportContentStatisticsXls = (params: ContentStatisticsExportParams) =>
  downloadStatisticsExport('xls', params)

export interface RecurringPeriodRow {
  recurrence_id: string
  recurrence__content_id: string
  recurrence__content__title: string
  recurrence__content__content_type: string
  recurrence__frequency: 'monthly' | 'yearly'
  recurrence__active: boolean
  period_start: string
  due_date: string
  assigned: number
  completed: number
  pending: number
  excused: number
}

export const getRecurringStatistics = async (params: { page: number; page_size: number; search: string; period?: string }) => {
  const response = await api.get('/content/recurring-statistics/', { params })
  return response.data
}

export const stopRecurrence = async (id: string) => {
  const response = await api.patch('/content/recurring-statistics/', { id, active: false })
  return response.data
}

export const exportRecurringStatisticsCsv = async (params: { search?: string; period?: string }) => {
  const response = await api.get('/content/recurring-statistics/export-csv/', { params, responseType: 'blob' })
  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8;' }))
  const match = String(response.headers['content-disposition'] || '').match(/filename="?([^"]+)"?/)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', match?.[1] || 'recurring-assignments.csv')
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}
